import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { Item } from "./api.ts";
import { KINDS, type Kind, kindClass, kindOf } from "./format.ts";
import { Icon } from "./souvenir.ts";

// The map's "What" filter: a chip that opens a checkbox per kind the trip has, with its count. Nothing
// checked shows everything.
export function KindFilter({
  items,
  kinds,
  compact,
  onChange,
}: {
  items: Item[];
  kinds: Kind[];
  // On a phone the chip keeps one width, so the filters stay on two rows: always "What", led by the
  // kind's icon or how many are on.
  compact: boolean;
  onChange: (kinds: Kind[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLFieldSetElement>(null);
  const id = useId();
  const counts = new Map<Kind, number>();
  for (const i of items) counts.set(kindOf(i), (counts.get(kindOf(i)) ?? 0) + 1);
  const present = KINDS.filter((k) => counts.has(k.key));
  const on = present.filter((k) => kinds.includes(k.key));

  // Closes on a tap or click anywhere else, and on Escape: this first, before the map's list or card
  // (caught on the way down, so the trip's own Escape never hears it).
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      chip.current?.focus();
    };
    addEventListener("pointerdown", away);
    addEventListener("keydown", onEscape, { capture: true });
    return () => {
      removeEventListener("pointerdown", away);
      removeEventListener("keydown", onEscape, { capture: true });
    };
  }, [open]);

  // Kept on the screen: shifted left when the chip sits near the right edge (measured on every render,
  // as the chip moves when its label or the row it wraps onto changes).
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!open || !chip.current || !menu.current) return;
    const right = chip.current.getBoundingClientRect().left + menu.current.offsetWidth;
    setShift(Math.max(0, right - (document.documentElement.clientWidth - 16)));
  });

  // One kind or none: nothing to choose between.
  if (present.length < 2) return null;
  const one = on.length === 1 ? on[0] : undefined;
  const label = one ? one.label : on.length === 0 ? "What" : `${on.length} kinds`;
  const toggle = (k: Kind) => onChange(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]);

  return (
    <div className="tm-kind-filter" ref={root}>
      <button
        ref={chip}
        type="button"
        className={`sv-chip sv-chip--pick tm-kind-chip${on.length > 0 ? " is-selected" : ""}`}
        aria-label={on.length === 0 ? "What" : `What: ${on.map((k) => k.label).join(", ")}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        {compact && on.length > 1 ? (
          <span className="tm-kind-count">{on.length}</span>
        ) : (
          <Icon name={one ? one.icon : "pin"} size={16} />
        )}
        <span>{compact ? "What" : label}</span>
        {!compact && <Icon name="chevron" size={14} className={`tm-kind-caret${open ? " is-open" : ""}`} />}
      </button>
      {open && (
        <fieldset className="tm-kind-menu" id={id} ref={menu} style={{ transform: `translateX(${-shift}px)` }}>
          <legend className="tm-hidden">What to show</legend>
          {present.map((k) => (
            <label key={k.key} className="tm-kind-row">
              <input type="checkbox" checked={kinds.includes(k.key)} onChange={() => toggle(k.key)} />
              {/* Its icon on its colour, as on the map: the dropdown is the map's legend too. */}
              <span className={`tm-kind-swatch ${kindClass(k.key)}`}>
                <Icon name={k.icon} size={14} />
              </span>
              <span className="callout">{k.label}</span>
              <span className="callout tm-muted">{counts.get(k.key)}</span>
            </label>
          ))}
          <div className="tm-kind-foot">
            <button type="button" className="tm-link caption" disabled={on.length === 0} onClick={() => onChange([])}>
              Clear
            </button>
          </div>
        </fieldset>
      )}
    </div>
  );
}
